/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactJson from 'react-json-view';

// material-ui
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Collapse,
    Drawer,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Switch,
    FormControlLabel,
    Tooltip,
    CircularProgress,
    Fab,
    Tabs,
    Tab,
    Divider,
    Skeleton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

// project imports
import { useDispatch, useSelector } from 'store';
import { getExecutionHistory, reprocess, redrive } from 'store/slices/stateMachines';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import StateMachineFlowGraph from './StateMachineFlowGraph';

// Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Webhook from '@mui/icons-material/Webhook';
import HourglassBottom from '@mui/icons-material/HourglassBottom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LoadingButton from '@mui/lab/LoadingButton';
import { IconRotate2, IconPlayerPlay } from '@tabler/icons';

// ==============================|| PROCESS LIST ||============================== //

const StatusChip = ({ row, theme, executionStatus }: { row: any; theme: any; executionStatus?: string }) => {
    // Correctly determine if the task ultimately succeeded, even with retries
    const hasSuccessEvent =
        row.events?.some((e: any) => e.type?.includes('Exited') || e.type?.includes('Succeeded')) ||
        (executionStatus === 'SUCCEEDED' && row.events?.length > 0 && !row.error);
    const isWaitFinished = row.type === 'Wait' && row.events?.length >= 2;
    const isSucceeded = hasSuccessEvent || isWaitFinished;

    if (row.isWaiting) {
        const statusColor = row.type === 'Wait' ? theme.palette.primary.main : theme.palette.warning.main;
        return (
            <Chip
                label={row.type === 'Wait' ? 'Delaying' : 'Waiting Webhook'}
                icon={row.type === 'Wait' ? <HourglassBottom fontSize="small" /> : <Webhook fontSize="small" />}
                color={row.type === 'Wait' ? 'primary' : 'warning'}
                variant="filled"
                sx={{
                    fontWeight: 700,
                    animation: 'pulse 2s infinite ease-in-out',
                    '@keyframes pulse': {
                        '0%': { opacity: 1, boxShadow: `0 0 0 0 ${alpha(statusColor, 0.4)}` },
                        '50%': { opacity: 0.8, boxShadow: `0 0 0 10px ${alpha(statusColor, 0)}` },
                        '100%': { opacity: 1, boxShadow: `0 0 0 0 ${alpha(statusColor, 0)}` }
                    }
                }}
            />
        );
    }

    if (isSucceeded) {
        const hasPriorFailures = row.events?.some(
            (e: any) => e.type.includes('Failed') || e.type.includes('Aborted') || e.type.includes('TimedOut')
        );
        const label = hasPriorFailures && row.type !== 'Wait' ? 'Success (Retried)' : 'Success';
        return <Chip label={label} color="success" variant="outlined" />;
    }

    const hasFailed = row.events?.find((e: any) => e.type.includes('Failed') || e.type.includes('Aborted') || e.type.includes('TimedOut'));
    if (hasFailed) return <Chip label="Fail" color="error" variant="outlined" />;

    return <Chip label="Running" color="primary" variant="outlined" />;
};

const MapProgress = ({ progress, theme }: { progress: any; theme: any }) => {
    if (!progress || (progress.total === 0 && !progress.isFinished)) return null;

    const hasFailures = progress.failed > 0;
    const isFinishedEmpty = progress.total === 0 && progress.isFinished;

    return (
        <Box sx={{ mt: 1.5, width: '100%', maxWidth: 220 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.62rem',
                        fontWeight: 900,
                        color: hasFailures ? 'error.main' : 'text.secondary',
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase'
                    }}
                >
                    {(() => {
                        if (hasFailures) return `${progress.failed} FAILURES`;
                        if (isFinishedEmpty) return 'EMPTY DATASET';
                        return 'ITERATIONS';
                    })()}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.65rem',
                        fontWeight: 900,
                        color: hasFailures ? 'error.main' : theme.palette.primary.main
                    }}
                >
                    {isFinishedEmpty ? '0/0' : `${progress.succeeded}/${progress.total}`}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={progress.percentage}
                sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(hasFailures ? theme.palette.error.main : theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: hasFailures ? theme.palette.error.main : theme.palette.primary.main
                    }
                }}
            />
        </Box>
    );
};

const Row = (props: any) => {
    const { row, index, executionStatus } = props;
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [detailData, setDetailData] = React.useState({ input: {}, output: {}, error: {} });
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [tabValue, setTabValue] = React.useState(0);
    const [jsonCopied, setJsonCopied] = React.useState(false);
    const err = row.type === 'Exception' || row.type === 'Fail' || row.type === 'Error';

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleCopyJson = (data: any) => {
        const text = JSON.stringify(data, null, 2);
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (copyError) {
                console.error('Fallback copy failed', copyError);
            }
            document.body.removeChild(textArea);
        }
        setJsonCopied(true);
        setTimeout(() => setJsonCopied(false), 2000);
    };

    const copyIcon = jsonCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />;

    const calculateDuration = (start?: string, end?: string) => {
        if (!start || !end) return '-';
        const diff = new Date(end).getTime() - new Date(start).getTime();
        const seconds = Math.floor(diff / 1000);
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m ${seconds % 60}s`;
    };

    return (
        <>
            <TableRow>
                <TableCell component="th" scope="row" colSpan={1}>
                    {((row?.children?.length || row?.length) && (
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )) || (
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            <RemoveIcon />
                        </IconButton>
                    )}
                </TableCell>
                <TableCell component="th" scope="row" colSpan={1}>
                    {row.name || index + 1}
                </TableCell>

                <TableCell component="th" scope="row" colSpan={1}>
                    {row.type}
                </TableCell>
                <TableCell component="th" scope="row" colSpan={1}>
                    <StatusChip row={row} theme={theme} executionStatus={executionStatus} />
                    {row.type === 'Map' && row.mapProgress && <MapProgress progress={row.mapProgress} theme={theme} />}
                    {row.error && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'error.main',
                                display: 'block',
                                mt: 0.5,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Tooltip title={row.error.cause || row.error.error || 'Unknown Error'}>
                                <span>{row.error.error || 'Error'}</span>
                            </Tooltip>
                        </Typography>
                    )}
                </TableCell>
                {row?.events?.length && (
                    <>
                        <TableCell component="th" scope="row" colSpan={1}>
                            {row?.events?.[0]?.timestamp && new Date(row?.events?.[0]?.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell component="th" scope="row" colSpan={1}>
                            {row?.events?.[1]?.timestamp && new Date(row?.events?.[1]?.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell component="th" scope="row" colSpan={1}>
                            {calculateDuration(row?.events?.[0]?.timestamp, row?.events?.[1]?.timestamp)}
                        </TableCell>
                        <TableCell component="th" scope="row" colSpan={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => {
                                    setDetailData({
                                        input: JSON.parse(
                                            row?.events?.find((item: any) => item?.stateEnteredEventDetails?.input)
                                                ?.stateEnteredEventDetails?.input || '{}'
                                        ),
                                        output: JSON.parse(
                                            row?.events?.find((item: any) => item?.stateExitedEventDetails?.output)?.stateExitedEventDetails
                                                ?.output || '{}'
                                        ),
                                        error: JSON.parse(
                                            row?.events?.find((item: any) => item?.executionFailedEventDetails?.cause)
                                                ?.executionFailedEventDetails?.cause || '{}'
                                        )
                                    });
                                    setOpenDrawer(true);
                                }}
                            >
                                Details
                            </Button>
                        </TableCell>
                    </>
                )}
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} component="td" scope="row" colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {row?.length && (
                                            <>
                                                <TableCell />
                                                <TableCell>Name</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Start Date</TableCell>
                                                <TableCell>End Date</TableCell>
                                                <TableCell>Duration</TableCell>
                                                <TableCell />
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row?.children?.length > 0 &&
                                        row?.children
                                            ?.filter((i: any) => i)
                                            ?.map((child: any, childIndex: number) => (
                                                <Row
                                                    key={child.name || childIndex}
                                                    row={child}
                                                    index={childIndex}
                                                    executionStatus={executionStatus}
                                                />
                                            ))}
                                    {row?.length > 0 &&
                                        row
                                            ?.filter((i: any) => i)
                                            ?.map((child: any, childIndex: number) => (
                                                <Row
                                                    key={child.name || childIndex}
                                                    row={child}
                                                    index={childIndex}
                                                    executionStatus={executionStatus}
                                                />
                                            ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Collapse>
                </TableCell>
            </TableRow>
            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', md: 600 },
                        bgcolor: theme.palette.background.default,
                        backgroundImage: 'none'
                    }
                }}
            >
                {openDrawer && (
                    <Box sx={{ h: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                p: 3,
                                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50]
                            }}
                        >
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {row.name || 'Task Detail'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={row.type} size="small" variant="outlined" />
                                <Typography variant="caption" color="textSecondary">
                                    Index: {index + 1}
                                </Typography>
                            </Stack>
                        </Box>

                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="task details tabs" sx={{ px: 2 }}>
                                <Tab label="Input" />
                                <Tab
                                    label="Output"
                                    sx={{
                                        color: row.events?.length < 2 && !row.error ? alpha(theme.palette.text.disabled, 0.5) : 'inherit'
                                    }}
                                />
                                <Tab
                                    label="Error"
                                    sx={{
                                        color: !row.error ? alpha(theme.palette.text.disabled, 0.5) : theme.palette.error.main
                                    }}
                                />
                            </Tabs>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                            {tabValue === 0 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                                            INPUT DATA
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={copyIcon}
                                            onClick={() => handleCopyJson(detailData.input)}
                                            color={jsonCopied ? 'success' : 'primary'}
                                        >
                                            {jsonCopied ? 'Copied!' : 'Copy JSON'}
                                        </Button>
                                    </Stack>
                                    <ReactJson
                                        src={detailData.input}
                                        theme="monokai"
                                        // @ts-ignore
                                        enableClipboard={(copy: any) => handleCopyJson(copy.src)}
                                        displayDataTypes={false}
                                        collapsed={2}
                                        style={{ background: 'transparent' }}
                                    />
                                </Box>
                            )}
                            {tabValue === 1 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.success.main, fontWeight: 700 }}>
                                            OUTPUT DATA
                                        </Typography>
                                        {!(row.events?.length < 2 && !row.error) && (
                                            <Button
                                                size="small"
                                                startIcon={copyIcon}
                                                onClick={() => handleCopyJson(detailData.output)}
                                                color={jsonCopied ? 'success' : 'primary'}
                                            >
                                                {jsonCopied ? 'Copied!' : 'Copy JSON'}
                                            </Button>
                                        )}
                                    </Stack>
                                    {row.events?.length < 2 && !row.error ? (
                                        <Typography variant="body2" color="textSecondary">
                                            No output available yet. Task might be running or failed early.
                                        </Typography>
                                    ) : (
                                        <ReactJson
                                            src={detailData.output}
                                            theme="monokai"
                                            // @ts-ignore
                                            enableClipboard={(copy: any) => handleCopyJson(copy.src)}
                                            displayDataTypes={false}
                                            collapsed={2}
                                            style={{ background: 'transparent' }}
                                        />
                                    )}
                                </Box>
                            )}
                            {tabValue === 2 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.error.main, fontWeight: 700 }}>
                                            ERROR DETAILS
                                        </Typography>
                                        {row.error && (
                                            <Button
                                                size="small"
                                                startIcon={copyIcon}
                                                onClick={() => handleCopyJson(detailData.error)}
                                                color={jsonCopied ? 'success' : 'primary'}
                                            >
                                                {jsonCopied ? 'Copied!' : 'Copy JSON'}
                                            </Button>
                                        )}
                                    </Stack>
                                    {!row.error ? (
                                        <Typography variant="body2" color="textSecondary">
                                            No errors detected for this task.
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            <Alert severity="error" variant="outlined" sx={{ borderRadius: '8px' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {row.error.error || 'Unknown Error'}
                                                </Typography>
                                                <Typography variant="body2">{row.error.cause || 'No cause provided'}</Typography>
                                            </Alert>
                                            <Typography variant="subtitle2" sx={{ mt: 2, opacity: 0.7 }}>
                                                RAW ERROR DATA
                                            </Typography>
                                            <ReactJson
                                                src={detailData.error}
                                                theme="monokai"
                                                // @ts-ignore
                                                enableClipboard={(copy: any) => handleCopyJson(copy.src)}
                                                displayDataTypes={false}
                                                style={{ background: 'transparent' }}
                                            />
                                        </Stack>
                                    )}
                                </Box>
                            )}
                        </Box>

                        <Divider />
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setOpenDrawer(false)}>Close</Button>
                        </Box>
                    </Box>
                )}
            </Drawer>
        </>
    );
};

const ExecutionSkeleton = () => (
    <MainCard content={false}>
        <Box sx={{ px: 3, py: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Skeleton variant="circular" width={40} height={40} sx={{ mt: 0.5 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="30%" height={24} />
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        <Skeleton variant="rectangular" width={110} height={38} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" width={100} height={38} sx={{ borderRadius: 2 }} />
                    </Stack>
                </Grid>
            </Grid>
        </Box>

        <Box sx={{ px: 0, py: 0 }}>
            <Skeleton variant="rectangular" width="100%" height={435} />
        </Box>

        <Box sx={{ p: 3 }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <TableCell key={i}>
                                    <Skeleton variant="text" width="60%" />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <TableRow key={i}>
                                <TableCell>
                                    <Skeleton variant="circular" width={28} height={28} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="90%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: 4 }} />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="60%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="40%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="100%" />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    </MainCard>
);

const StateMachineExecutionHistoryCard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [countDown, setCountDown] = React.useState(100);
    const [copied, setCopied] = React.useState(false);
    const { executionHistoryData, executionDetail, loading, definition } = useSelector((state) => state.stateMachines);
    const { stateMachineId, stateMachineExecutionId } = useParams();

    const handleCopy = () => {
        navigator.clipboard.writeText(stateMachineExecutionId || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const err =
        executionHistoryData?.find((item) => item?.type === 'Error') || executionHistoryData?.find((item) => item?.type === 'Exception');

    React.useEffect(() => {
        if (stateMachineId && stateMachineExecutionId) dispatch(getExecutionHistory(stateMachineId, stateMachineExecutionId));
    }, [dispatch, stateMachineId, stateMachineExecutionId]);

    React.useEffect(() => {
        let interval: any;
        if (autoRefresh && stateMachineId && stateMachineExecutionId) {
            interval = setInterval(() => {
                setCountDown((prev) => {
                    if (prev <= 0) {
                        dispatch(getExecutionHistory(stateMachineId, stateMachineExecutionId, true));
                        return 100;
                    }
                    return prev - 100 / (30 / 0.1); // 30s cycle
                });
            }, 100);
        } else {
            setCountDown(100);
        }
        return () => clearInterval(interval);
    }, [autoRefresh, stateMachineId, stateMachineExecutionId, dispatch]);

    const handleReprocess = async () => {
        if (stateMachineId && stateMachineExecutionId) await dispatch(reprocess(stateMachineId, stateMachineExecutionId));
    };

    const handleRedrive = async () => {
        if (stateMachineId && stateMachineExecutionId) await dispatch(redrive(stateMachineId, stateMachineExecutionId));
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SUCCEEDED':
                return 'success';
            case 'FAILED':
            case 'ABORTED':
            case 'TIMED_OUT':
                return 'error';
            default:
                return 'primary';
        }
    };

    if (loading && !executionHistoryData?.length) return <ExecutionSkeleton />;

    return (
        <MainCard content={false}>
            <Box
                sx={{
                    px: 3,
                    py: 2.5
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <IconButton onClick={() => navigate(`/stateMachines/${stateMachineId}`)} sx={{ mt: 0.5 }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: 900,
                                            wordBreak: 'break-all',
                                            letterSpacing: '-0.5px',
                                            mr: 1
                                        }}
                                    >
                                        {stateMachineExecutionId}
                                    </Typography>

                                    <Tooltip title={copied ? 'Copied!' : 'Copy Execution ID'}>
                                        <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'primary'} sx={{ mr: 1 }}>
                                            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>

                                    {executionDetail?.status && (
                                        <Chip
                                            label={executionDetail.status}
                                            color={getStatusColor(executionDetail.status)}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                fontWeight: 800,
                                                borderWidth: 2,
                                                '& .MuiChip-label': { px: 1.5 }
                                            }}
                                        />
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 0.5, opacity: 0.7 }}>
                                    {stateMachineId} / History
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Grid container spacing={1} justifyContent="flex-end" alignItems="center">
                            <Grid item>
                                <Tooltip title="Start a fresh execution from the beginning with the same input. This creates a new execution record separate from the current one. Can be triggered at any time.">
                                    <span>
                                        <LoadingButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            startIcon={<IconRotate2 />}
                                            loading={loading}
                                            onClick={async () => handleReprocess()}
                                        >
                                            Reprocess
                                        </LoadingButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Retry the execution from the failed step. This resumes the current execution and maintains its state history.">
                                    <span>
                                        <LoadingButton
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            startIcon={<PlayArrowIcon />}
                                            disabled={executionDetail?.redriveStatus !== 'REDRIVABLE'}
                                            loading={loading}
                                            onClick={async () => handleRedrive()}
                                        >
                                            Redrive
                                        </LoadingButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                            <Grid item sx={{ display: 'flex', justifyContent: 'center', ml: 1 }}>
                                <Tooltip title="Live Mode (30s)">
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress value={countDown} variant="determinate" size={32} />
                                        <Box
                                            sx={{
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Fab
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    boxShadow: 'none',
                                                    width: 24,
                                                    height: 24,
                                                    minHeight: 24,
                                                    '& svg': { width: 16, height: 16 }
                                                }}
                                                onClick={() => setAutoRefresh(!autoRefresh)}
                                            >
                                                {!autoRefresh && <IconPlayerPlay />}
                                                {autoRefresh && <PauseIcon />}
                                            </Fab>
                                        </Box>
                                    </Box>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <CardContent sx={{ p: 0 }}>
                <StateMachineFlowGraph
                    definition={definition}
                    executionHistoryData={executionHistoryData}
                    executionStatus={executionDetail?.status}
                />
                <Grid container spacing={gridSpacing} sx={{ px: 3, pt: 2 }}>
                    {executionDetail?.error && (
                        <Grid item xs={12}>
                            <Alert
                                severity="error"
                                variant="filled"
                                sx={{
                                    borderRadius: '12px',
                                    '& .MuiAlert-message': { width: '100%' }
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700 }}>
                                    Execution Failed: {executionDetail.error.error}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                    {executionDetail.error.cause}
                                </Typography>
                            </Alert>
                        </Grid>
                    )}

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {executionHistoryData?.map((row, index) => (
                                    <Row key={index} index={index} row={row} executionStatus={executionDetail?.status} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </CardContent>
        </MainCard>
    );
};

export default StateMachineExecutionHistoryCard;
